import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { HowToContent } from "./howTo";

/**
 * Generic "How to play" dialog. Driven by a `HowToContent` record from
 * `howTo.ts` so every game shares the same structure and tone.
 *
 * Behavior:
 *  - Shown manually via the `<GameMenu />` "How to play" button.
 *  - Auto-shown once per device on first visit - handled by the consumer
 *    using `isOnboarded` / `markOnboarded` from `howTo.ts`.
 */
export default function HowToPlayDialog({
  open,
  onOpenChange,
  content,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  content: HowToContent;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{content.title}</DialogTitle>
          <DialogDescription>{content.intro}</DialogDescription>
        </DialogHeader>

        <ol className="space-y-3 text-sm">
          {content.steps.map((step, i) => (
            <li key={i} className="flex gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-xs font-bold text-primary font-mono">
                {i + 1}
              </div>
              <div>
                <div className="font-semibold text-foreground">{step.title}</div>
                <p className="text-muted-foreground mt-0.5 text-[13px] leading-relaxed">
                  {step.body}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="w-full">
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
